import { Spinner } from "@chakra-ui/react";
import { apiGetErrorDetail } from "@raiden/library/helpers/api";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { Fragment, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSearchInfiniteContext } from "../../../containers/SearchInfinite/useSearchInfiniteContext";
import Splash from "../../Splash";

/**
 * @template [T=any]
 * @param {object} props
 * @param {import("swr/infinite").SWRInfiniteResponse<T>} props.swrInfiniteResponse the SWR response
 * @param {boolean} [props.shouldRenderWithEmptyData] if true, the children will be rendered even if the data is empty
 * @param {import("react").ReactElement} [props.noResult] the element to render if the data is empty
 * @param {import("react").ReactElement} [props.loading] the element to render if the data is loading
 * @param {(children: import("react").ReactNode) => import("react").ReactElement} [props.wrapper] the wrapper that will be used to render any interal elements
 * @param {(response: T[]) => import("react").ReactElement} props.children
 */
function SWRInfiniteHandler({
  swrInfiniteResponse,
  shouldRenderWithEmptyData = false,
  noResult,
  loading,
  wrapper = (children) => <>{children}</>,
  children,
}) {
  const intl = useIntl();

  const { form } = useSearchInfiniteContext();

  const Loading = useMemo(() => {
    return loading ?? wrapper(<Spinner />);
  }, [loading, wrapper]);

  if (
    form.formState.isDirty ||
    (swrInfiniteResponse.data === undefined &&
      swrInfiniteResponse.error === undefined) ||
    (swrInfiniteResponse.isValidating && swrInfiniteResponse.data === undefined)
  ) {
    return Loading;
  }

  if (swrInfiniteResponse.data !== undefined) {
    const apiResponses = swrInfiniteResponse.data;
    if (
      apiResponses.length === 1 &&
      // @ts-ignore
      (apiResponses[0]?.data?.length ?? 0) === 0 &&
      !shouldRenderWithEmptyData
    ) {
      return (
        noResult ??
        wrapper(
          <Splash
            src={generateFrontPath({
              id: "internal-assets",
              parameters: { filePath: "illustrations/empty.svg" },
              includeBasePath: true,
            })}
            title={intl.formatMessage({ defaultMessage: "Aucun résultat" })}
          />,
        )
      );
    }
    return children(swrInfiniteResponse.data);
  }

  if (!swrInfiniteResponse.isValidating && swrInfiniteResponse.error) {
    return wrapper(
      <Splash
        src={generateFrontPath({
          id: "internal-assets",
          parameters: { filePath: "illustrations/error.svg" },
          includeBasePath: true,
        })}
        title={intl.formatMessage({
          defaultMessage: "Une erreur est survenue",
        })}
        description={apiGetErrorDetail({ error: swrInfiniteResponse.error })}
      />,
    );
  }

  return null;
}

export default SWRInfiniteHandler;
